import { Sparkles, AlertTriangle } from "lucide-react";
import { useGetDashboardSummary } from "@workspace/api-client-react";
import {
  AI_LIMIT_WARN_THRESHOLD,
  formatResetCountdown,
  useNowTick,
} from "@/lib/aiRateLimit";
import { cn } from "@/lib/utils";

interface AiRateLimitInlineProps {
  className?: string;
}

export function AiRateLimitInline({ className }: AiRateLimitInlineProps) {
  const { data } = useGetDashboardSummary();
  const now = useNowTick(1000);

  if (!data) return null;
  const remaining = data.aiRateLimitRemaining;
  const max = data.aiRateLimitMax;
  const resetAt = data.aiRateLimitResetAt;
  if (typeof remaining !== "number" || typeof max !== "number") return null;

  const exhausted = remaining <= 0;
  const low = remaining <= AI_LIMIT_WARN_THRESHOLD;
  const countdown = resetAt ? formatResetCountdown(resetAt, now) : null;

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs",
        exhausted
          ? "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"
          : low
            ? "border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-200"
            : "border-border bg-muted/40 text-muted-foreground",
        className,
      )}
      data-testid="ai-rate-limit-inline"
      title={
        countdown
          ? `${remaining} of ${max} AI requests left this hour · resets in ${countdown}`
          : `${remaining} of ${max} AI requests left this hour`
      }
    >
      {exhausted || low ? (
        <AlertTriangle className="w-3 h-3 shrink-0" />
      ) : (
        <Sparkles className="w-3 h-3 shrink-0" />
      )}
      <span>
        {remaining} / {max} AI requests left
        {countdown ? ` · resets in ${countdown}` : ""}
      </span>
    </div>
  );
}
